import {
  BufferGeometry,
  DoubleSide,
  Float32BufferAttribute,
  Ray,
  Uint32BufferAttribute,
  Vector3,
} from 'three';
import { MeshBVH } from 'three-mesh-bvh';
import { depthToIso, type DistanceField } from './levelSetOffset';

/**
 * Turns an open sheet (a crown scan plus its generated interproximal walls)
 * into a watertight printable shell.
 *
 * The OUTER surface simply IS the source: same vertices, same triangles, zero
 * deviation by construction. That is the whole reason this exists instead of
 * the full voxel route, which rebuilds the outer surface from scratch (SDF →
 * slab → marching cubes → smooth → reproject) and prints back every ridge the
 * round trip invented.
 *
 * The INNER surface is a different question, and the answer is the standard
 * one: `fit: 'levelset'` makes it the iso-surface of a distance field at
 * −thickness, sampled onto this mesh's topology (levelSetOffset.ts). A level
 * set cannot fold, cannot escape, and thins to solid where the material runs
 * out — so that mode carries none of the repair passes the per-vertex modes
 * below need. Voxels are used ONLY there, for a surface no foil ever touches.
 *
 * The inner surface is the same mesh pushed along its vertex normals, so the
 * two share a topology and the rim between them is a trivial bridge — no
 * matching of two unrelated marching-cubes borders.
 *
 * What a naive per-vertex offset gets wrong is sharp convex ridges (cusp tips,
 * incisal edges) and thin material (the sliver between a generated
 * interproximal wall and the crown beside it). In the first the two flanks
 * offset through each other; in the second the wall comes out the far side.
 * That second one is the whole ballgame — a wall that exits through the outer
 * surface is a fault someone can SEE, and it is what the voxel export was
 * doing too, since a marching-cubes slab is an offset like any other.
 *
 * So the inner surface is not simply pushed in. It is relaxed onto the surface
 * a distance field would draw (see relaxOffsetSurface), and then anything
 * still poking out is tucked back under. `outsideVertices` is the number that
 * matters; folds that stay buried in material cannot reach the part.
 */
export type OffsetShellOptions = {
  /** Wall thickness in mm — the offset the shell gets wherever it fits. */
  thicknessMm: number;
  /**
   * 'relaxed' (default) is the one described above: smooth and reproject until
   * the inner surface is the distance-field one, then tuck in the escapees.
   * Full thickness nearly everywhere, sharp tips left solid rather than
   * hollow-and-thin.
   *
   * 'guarded' instead pulls the offset back wherever it would fold — cheaper
   * and fully deterministic, but the wall thins over a good quarter of the
   * crown to buy it. Kept as the fallback if relaxation ever misbehaves on a
   * jaw.
   *
   * 'plain' is Blender's Solidify: every face extruded by the full thickness,
   * nothing checked and nothing denoised. Faster, perfectly even where the
   * crown allows it, and it WILL fold through itself at sharp ridges and come
   * out the far side of thin material — measured on real crowns it leaves up
   * to 52 vertices poking through the outer surface by as much as 1 mm, which
   * is exactly the artifact the voxel export used to print. Kept as the plain
   * answer to compare against.
   *
   *
   * 'shrink' does not use the normals at all: it scales the whole mesh about
   * its own centre. The inner surface is then a similar copy of the outer one
   * and provably cannot self-intersect — measured, exactly zero folded faces
   * on all fourteen teeth, where every other mode has some.
   *
   * It is nonetheless the worst of the lot, and kept only so nobody has to
   * find that out twice. A crown is not star-shaped about a point: the axial
   * walls run nearly parallel to the radius, so scaling slides the copy ALONG
   * the surface instead of into it, and at the cervical flare and the
   * interproximal walls it lands outside altogether. Measured raw: 6484
   * vertices outside the crown by up to 4.6 mm, against 152 for a plain
   * extrude. Only 3 % of the wall comes out within 10 % of target, because
   * thickness follows the distance from the centre.
   */
  /**
   * 'levelset' is the standard construction and the production default: the
   * inner wall is the iso-surface of a signed distance field at −thickness,
   * sampled onto this mesh's own topology (see levelSetOffset.ts). It needs
   * `field`. The others are the historical per-vertex offsets, kept for
   * comparison — see the mode notes on buildOffsetShell.
   */
  fit?: 'levelset' | 'relaxed' | 'guarded' | 'plain' | 'shrink';
  /** Required by `fit: 'levelset'`: the solid's signed distance field. */
  field?: DistanceField;
  /**
   * Blender's "even thickness". Along a crease the vertex normal bisects the
   * faces, so a plain vertex offset leaves them only t·cos θ apart; dividing
   * it out puts the wall back on target face to face.
   *
   * Default follows `fit`: on for 'plain', where it is what Blender does and
   * there is nothing to lose, off for 'guarded'. It only ever pushes the
   * offset deeper, and at a crease that is exactly where the fold guard is
   * already fighting — measured, it cost about six points of the crown
   * carrying a full-thickness wall to buy a correction on a small fraction
   * of it.
   */
  evenThickness?: boolean;
  /** Cap on that division, for spikes where the cosine goes to nothing. */
  evenThicknessClamp?: number;
  /**
   * For `fit: 'shrink'`: the cosine below which the direction to the centre
   * stops being trusted and blends over to the vertex normal. Wider trades
   * folds — a pure radial field is a similarity and cannot fold — for a wall
   * that does not collapse where the surface faces sideways.
   */
  radialBlend?: number;
  /** Smooth-and-reproject rounds for `fit: 'relaxed'`. */
  relaxPasses?: number;
  /**
   * Passes of smoothing on the per-vertex step LENGTH before the escape
   * repair. Positions never move sideways — the field of offset distances is
   * what gets evened out, so the inner wall follows the outer shape with a
   * smoothly varying offset instead of pitting wherever caps, taper cuts and
   * projections disagree between neighbours.
   */
  stepSmoothingPasses?: number;
  /**
   * Rounds of tucking escaped inner vertices back under the surface. Set to 0
   * to see what a mode does on its own — 'plain' always runs at 0, since the
   * point of it is to be an unguarded offset.
   */
  escapeRounds?: number;
  /**
   * Floor for the tapered thickness at ridges. Kept above zero on purpose: a
   * vertex offset to nothing produces a degenerate face, which slicers handle
   * far worse than a very thin one.
   */
  minThicknessMm?: number;
  /** Safety margin on the wall where thin material caps it. */
  fitFactor?: number;
  /** Passes of pulling folded faces back. Each takes 30% off what it touches. */
  maxIterations?: number;
  /**
   * Cap on how fast the thickness may change between neighbouring vertices,
   * as a fraction of their distance. Below 1 the inner surface cannot fold
   * even where the taper kicks in hard.
   */
  lipschitz?: number;
  /**
   * Smoothing passes applied to the surface the INNER wall is built from. The
   * outer surface is never touched by this.
   *
   * Scanner noise is a field of tiny convex bumps, and a bump of amplitude a
   * and wavelength λ curves at a radius of about λ²/4π²a — 0.2 mm for 10 µm of
   * noise at 0.3 mm, which is sharper than the wall is thick. Taken literally
   * that forces the wall to taper over a quarter of the crown for no
   * anatomical reason. The inner surface does not have to reproduce the
   * scan's noise, so it is offset from a smoothed copy instead and the taper
   * is left for features that are really there. Whatever the smoothing moves
   * is taken back out of the offset, so the printed wall stays on target.
   */
  innerSmoothingPasses?: number;
};

export type OffsetShellResult = {
  geometry: BufferGeometry;
  /** Source vertices; the shell's inner copy of vertex v is v + vertexCount. */
  vertexCount: number;
  /** Source faces; the shell's inner copy of face f is f + faceCount. */
  faceCount: number;
  /** Achieved wall thickness per source vertex, mm. */
  thickness: Float32Array;
  minThicknessMm: number;
  medianThicknessMm: number;
  /**
   * Vertices whose wall came out more than a tenth under target. Not a
   * tighter threshold, because the wall is measured perpendicular to the
   * FACETED surface and its triangles are chords sitting just inside the
   * smooth one, so every vertex on a curved crown reads a few microns short.
   */
  taperedVertices: number;
  /**
   * Inner faces that came out wound against the surface they were offset
   * from — where the offset folded through itself.
   */
  foldedFaces: number;
  /**
   * How far below the outer surface the deepest fold sits, mm — i.e. how much
   * material is between it and anything anyone can see.
   */
  foldDepthMm: number;
  /**
   * How big the largest fold is, mm: the longest edge of a folded triangle.
   * This is the number that says whether a fold matters — a fold finer than
   * the printer's pixel cannot come out in the part however deep it sits.
   */
  foldSpanMm: number;
  /**
   * Inner-surface vertices that ended up OUTSIDE the crown by more than
   * `ESCAPE_EPSILON`, and how far the worst one got. The numbers that say
   * whether a fault can show on the part: everything else the inner wall does
   * is buried in material and invisible, but a vertex out here has pushed the
   * wall through the surface someone is going to look at.
   *
   * The threshold is not fussiness — near the cervical rim the nearest face to
   * a point is the border itself and its normal answers at random, so the last
   * few microns of this test are noise. Read the depth, not the count.
   */
  outsideVertices: number;
  outsideDepthMm: number;
  /** Which ones — dump them as points to see where the fault lives. */
  outsideVertexIds: Uint32Array;
  /**
   * Worst ratio between an edge of the inner surface and the edge it came
   * from. This is what a spike IS: one vertex dragged away from its
   * neighbours, leaving a triangle stretched across the tooth. An offset that
   * behaves keeps this near 1 — the inner surface is barely smaller than the
   * outer one.
   */
  stretchRatio: number;
  /** Inner edges more than three times their source AND long enough to see. */
  stretchedEdges: number;
  /** The longest of them, mm — how far across the tooth the worst one reaches. */
  stretchSpanMm: number;
  /**
   * Inner edges whose MIDPOINT is outside the crown. Testing vertices is not
   * enough: a long edge can have both ends buried and still bulge out through
   * the surface in between, which is exactly what a spike looks like.
   */
  outsideEdges: number;
  /**
   * Inner vertices that left their neighbours behind: further from the mean of
   * their ring than the same vertex is on the OUTER surface (so honest
   * curvature does not count) by more than JAG_SPAN. This is the number that
   * sees a spike the way an eye does — the escape metrics only see what
   * crosses the outer surface, and a torn rim skirt never does. It is how the
   * rim tearing shipped: 300 of these sat on the border at full wall
   * thickness while every escape metric read clean.
   */
  jaggedVertices: number;
  /**
   * Vertices the thin-cavity rescue deepened past the opposing inner sheet:
   * the solid there was thinner than two walls, so instead of squeezing a
   * paper-thin cavity slot between the sheets (the slicer renders that as
   * holes along the gum-tooth junction) the region prints SOLID.
   */
  solidifiedVertices: number;
  /** Directed border edges bridged into the rim. */
  borderEdges: number;
  borderLoops: number;
  /** True when the source turned out to be wound inwards and was reversed. */
  flipped: boolean;
  /**
   * Per-vertex diagnosis fields, for measuring WHY a wall came out thin:
   * `capField` is what the material cap allowed, `targetField` what was asked,
   * `stepField` how far the vertex actually went, `solidField` whether the
   * rescue buried it in solid. Cheap (one float per vertex each) and exactly
   * what a thin-wall investigation needs, so they always ship.
   */
  capField: Float32Array;
  targetField: Float32Array;
  stepField: Float32Array;
  solidField: Uint8Array;
  /**
   * `fit: 'levelset'` only: vertices whose iso-surface did not exist locally,
   * i.e. where the solid is thinner than two walls and the region therefore
   * prints solid. Not an error — the count is a map of how much of the model
   * is too thin to hollow.
   */
  isoStalledVertices: number;
};

/** Relative slack on the fit test, against ordinary floating-point noise. */
const FIT_EPSILON = 1e-3;
/** Fraction of its original area an offset face may keep before it counts as folded. */
const COLLAPSE_RATIO = 0.1;
/** How far a folded face's vertices are pulled back each pass. */
const PULLBACK = 0.7;
/** Rounds of local smoothing that settle the creases a relaxed offset leaves. */
const POLISH_ROUNDS = 24;
/** An inner edge shorter than this cannot show in a print however stretched it is, mm. */
const STRETCH_SPAN = 0.2;
/** Below this cosine the radial direction stops being trusted, in favour of the normal. */
const RADIAL_BLEND = 0.35;
/** How far outside the crown an inner vertex may read before it counts, mm. */
const ESCAPE_EPSILON = 0.02;
/** Rounds of pulling escaped inner vertices back under the surface. */
const ESCAPE_ROUNDS = 6;
/** How fast an escape repair's cut may fade out, per mm of surface distance. */
const REPAIR_SLOPE = 0.7;
/** How far a vertex may sit from its neighbours' mean before it reads as a spike, mm. */
const JAG_SPAN = 0.15;
/**
 * The slope the material caps are graded at. Steeper than the repair's: every
 * notch of grading here costs wall thickness around EVERY thin sliver, and at
 * 0.7 the thin tail visibly grew (p5 of the wall fell from 0.45 to 0.17 mm)
 * for cliffs that were only just over the visibility threshold.
 */
const CAP_SLOPE = 1.4;
/**
 * The slope the level-set depths are graded at. Gentler than the material
 * caps': there is no thin-sliver tail to protect here, only medial ridges to
 * bridge, and every notch of grading costs wall thickness around each of them.
 */
const LEVELSET_SLOPE = 0.5;

type Sheet = {
  points: Float64Array;
  faces: Uint32Array;
  vertexCount: number;
  faceCount: number;
};

function readSheet(sheet: BufferGeometry): Sheet {
  const position = sheet.getAttribute('position');
  const index = sheet.getIndex();
  if (!index) throw new Error('buildOffsetShell: the sheet must be indexed and welded');
  const vertexCount = position.count;
  const points = new Float64Array(vertexCount * 3);
  for (let v = 0; v < vertexCount; v++) {
    points[v * 3] = position.getX(v);
    points[v * 3 + 1] = position.getY(v);
    points[v * 3 + 2] = position.getZ(v);
  }
  const faces = new Uint32Array(index.count);
  for (let i = 0; i < index.count; i++) faces[i] = index.getX(i);
  return { points, faces, vertexCount, faceCount: index.count / 3 };
}

/** Neighbour lists and an open-border mask, in CSR form. */
function connectivity({ faces, vertexCount, faceCount }: Sheet) {
  const seen = new Set<number>();
  const pairs: number[] = [];
  const degree = new Uint32Array(vertexCount);
  for (let f = 0; f < faceCount; f++) {
    for (let e = 0; e < 3; e++) {
      const i = faces[f * 3 + e];
      const j = faces[f * 3 + ((e + 1) % 3)];
      const key = Math.min(i, j) * vertexCount + Math.max(i, j);
      if (seen.has(key)) continue;
      seen.add(key);
      pairs.push(i, j);
      degree[i]++;
      degree[j]++;
    }
  }
  const start = new Uint32Array(vertexCount + 1);
  for (let v = 0; v < vertexCount; v++) start[v + 1] = start[v] + degree[v];
  const neighbours = new Uint32Array(pairs.length);
  const cursor = Uint32Array.from(start);
  for (let p = 0; p < pairs.length; p += 2) {
    neighbours[cursor[pairs[p]]++] = pairs[p + 1];
    neighbours[cursor[pairs[p + 1]]++] = pairs[p];
  }

  const directed = new Set<number>();
  for (let f = 0; f < faceCount; f++) {
    for (let e = 0; e < 3; e++) {
      directed.add(faces[f * 3 + e] * vertexCount + faces[f * 3 + ((e + 1) % 3)]);
    }
  }
  const onBorder = new Uint8Array(vertexCount);
  for (let f = 0; f < faceCount; f++) {
    for (let e = 0; e < 3; e++) {
      const i = faces[f * 3 + e];
      const j = faces[f * 3 + ((e + 1) % 3)];
      if (!directed.has(j * vertexCount + i)) {
        onBorder[i] = 1;
        onBorder[j] = 1;
      }
    }
  }
  return { start, neighbours, onBorder };
}

/**
 * The surface the inner wall is offset from: the source with its scan noise
 * taken off. Border vertices stay put so the cervical rim keeps its shape.
 *
 * Plain Laplacian, not Taubin — Taubin's second pass is what keeps it from
 * shrinking, but it overshoots at a curvature step and carves a crease into
 * the base where the source had none, which then folds. At a handful of passes
 * the shrinkage this trades for is well under the clamp below.
 *
 * Uniform weights, and then a hard clamp on how far any vertex may end up
 * from where it started. Inverse-edge-length weights look like the principled
 * answer on a mesh whose triangles vary — they stop a vertex between a 0.01 mm
 * ring and a 0.25 mm one being dragged a fifth of a millimetre — but measured
 * on the scans they denoise visibly worse (a quarter of the crown lost its
 * full-thickness wall), and the clamp deals with the dragging anyway. This is
 * meant to take noise off, not to reshape anything.
 */
function smoothedBase(sheet: Sheet, passes: number, maxShift: number): Float64Array {
  const base = Float64Array.from(sheet.points);
  if (passes <= 0) return base;
  const { points, vertexCount } = sheet;
  const { start, neighbours, onBorder } = connectivity(sheet);
  const next = new Float64Array(base.length);
  const relax = (weight: number) => {
    for (let v = 0; v < vertexCount; v++) {
      const from = start[v];
      const to = start[v + 1];
      if (onBorder[v] || to === from) {
        for (let k = 0; k < 3; k++) next[v * 3 + k] = base[v * 3 + k];
        continue;
      }
      const mean = [0, 0, 0];
      for (let n = from; n < to; n++) {
        const other = neighbours[n] * 3;
        for (let k = 0; k < 3; k++) mean[k] += base[other + k];
      }
      for (let k = 0; k < 3; k++) {
        next[v * 3 + k] = base[v * 3 + k] + weight * (mean[k] / (to - from) - base[v * 3 + k]);
      }
    }
    base.set(next);
  };
  for (let pass = 0; pass < passes; pass++) relax(0.5);

  for (let v = 0; v < vertexCount; v++) {
    const dx = base[v * 3] - points[v * 3];
    const dy = base[v * 3 + 1] - points[v * 3 + 1];
    const dz = base[v * 3 + 2] - points[v * 3 + 2];
    const moved = Math.hypot(dx, dy, dz);
    if (moved <= maxShift) continue;
    const scale = maxShift / moved;
    base[v * 3] = points[v * 3] + dx * scale;
    base[v * 3 + 1] = points[v * 3 + 1] + dy * scale;
    base[v * 3 + 2] = points[v * 3 + 2] + dz * scale;
  }
  return base;
}

/**
 * Blender's "even thickness": along a crease the vertex normal bisects the two
 * faces, so moving a vertex by t leaves the faces only t·cos θ apart. Dividing
 * by that cosine puts the wall back on target measured face to face — which is
 * how thickness is actually read off a print.
 *
 * Clamped, because on a spike the cosine goes to zero and the offset would
 * shoot off to infinity. Blender leaves that to the user; here it is capped.
 */
function evenThicknessFactors(
  { points, faces, vertexCount, faceCount }: Sheet,
  normals: Float64Array,
  clamp: number,
): Float32Array {
  const corner = [new Vector3(), new Vector3(), new Vector3()];
  const faceNormal = new Vector3();
  const toNext = new Vector3();
  const toPrev = new Vector3();
  const weightSum = new Float64Array(vertexCount);
  const cosineSum = new Float64Array(vertexCount);
  for (let f = 0; f < faceCount; f++) {
    for (let e = 0; e < 3; e++) corner[e].fromArray(points, faces[f * 3 + e] * 3);
    faceNormal
      .crossVectors(
        toNext.subVectors(corner[1], corner[0]),
        toPrev.subVectors(corner[2], corner[0]),
      )
      .normalize();
    for (let e = 0; e < 3; e++) {
      const v = faces[f * 3 + e];
      toNext.subVectors(corner[(e + 1) % 3], corner[e]);
      toPrev.subVectors(corner[(e + 2) % 3], corner[e]);
      // Weighted by the corner angle, so a fan of slivers does not outvote the
      // face the vertex really lies on.
      const angle = toNext.angleTo(toPrev);
      if (!Number.isFinite(angle)) continue;
      weightSum[v] += angle;
      cosineSum[v] +=
        angle *
        (faceNormal.x * normals[v * 3] +
          faceNormal.y * normals[v * 3 + 1] +
          faceNormal.z * normals[v * 3 + 2]);
    }
  }
  const factors = new Float32Array(vertexCount).fill(1);
  for (let v = 0; v < vertexCount; v++) {
    if (weightSum[v] <= 0) continue;
    const cosine = cosineSum[v] / weightSum[v];
    if (cosine > 1 / clamp) factors[v] = 1 / cosine;
    else if (cosine > 0) factors[v] = clamp;
  }
  return factors;
}

/**
 * Area-weighted vertex normals pointing OUT of the material.
 *
 * Which way that is, is the one thing the sheet does not say for itself.
 * Closing it to its own centroid and summing the tetrahedra gives the answer:
 * a cup wound outwards encloses positive volume. The cervical opening leaves
 * the total incomplete but never flips its sign — the crown dominates it by
 * an order of magnitude.
 */
function outwardNormals({ points, faces, vertexCount, faceCount }: Sheet): {
  normals: Float64Array;
  flipped: boolean;
} {
  const centre = new Vector3();
  for (let v = 0; v < vertexCount; v++) {
    centre.x += points[v * 3];
    centre.y += points[v * 3 + 1];
    centre.z += points[v * 3 + 2];
  }
  centre.divideScalar(Math.max(vertexCount, 1));

  const a = new Vector3();
  const b = new Vector3();
  const c = new Vector3();
  const ab = new Vector3();
  const ac = new Vector3();
  const faceNormal = new Vector3();
  const normals = new Float64Array(vertexCount * 3);
  let enclosed = 0;
  for (let f = 0; f < faceCount; f++) {
    const corners = [faces[f * 3], faces[f * 3 + 1], faces[f * 3 + 2]];
    a.fromArray(points, corners[0] * 3);
    b.fromArray(points, corners[1] * 3);
    c.fromArray(points, corners[2] * 3);
    faceNormal.crossVectors(ab.subVectors(b, a), ac.subVectors(c, a));
    // Area-weighted on purpose: the normal a vertex gets should follow the
    // surface it sits on, not the sliver triangles that happen to touch it.
    for (const i of corners) {
      normals[i * 3] += faceNormal.x;
      normals[i * 3 + 1] += faceNormal.y;
      normals[i * 3 + 2] += faceNormal.z;
    }
    enclosed +=
      ((a.x - centre.x) * faceNormal.x +
        (a.y - centre.y) * faceNormal.y +
        (a.z - centre.z) * faceNormal.z) /
      6;
  }

  const flipped = enclosed < 0;
  const normal = new Vector3();
  for (let v = 0; v < vertexCount; v++) {
    normal.fromArray(normals, v * 3).multiplyScalar(flipped ? -1 : 1);
    if (normal.lengthSq() < 1e-20) normal.set(0, 0, 0);
    else normal.normalize();
    normals[v * 3] = normal.x;
    normals[v * 3 + 1] = normal.y;
    normals[v * 3 + 2] = normal.z;
  }
  return { normals, flipped };
}

/**
 * A few passes of averaging over the normal field. An offset direction needs
 * to vary smoothly along the surface far more than it needs to match each
 * vertex's own faces — high-frequency disagreement between neighbouring
 * normals is exactly what tears the inner surface — and at the border, where
 * half the fan is missing and the generated interproximal wall's triangles
 * outvote the scan in what is left, averaging is the only place a sane
 * direction can come from at all.
 */
function diffuseNormals(
  vertexCount: number,
  normals: Float64Array,
  start: Uint32Array,
  neighbours: Uint32Array,
  passes: number,
): void {
  const next = new Float64Array(normals.length);
  const mean = new Vector3();
  const own = new Vector3();
  for (let pass = 0; pass < passes; pass++) {
    for (let v = 0; v < vertexCount; v++) {
      const from = start[v];
      const to = start[v + 1];
      own.fromArray(normals, v * 3);
      if (to === from) {
        own.toArray(next, v * 3);
        continue;
      }
      mean.set(0, 0, 0);
      for (let n = from; n < to; n++) {
        mean.x += normals[neighbours[n] * 3];
        mean.y += normals[neighbours[n] * 3 + 1];
        mean.z += normals[neighbours[n] * 3 + 2];
      }
      mean.divideScalar(to - from).add(own);
      if (mean.lengthSq() < 1e-12) mean.copy(own);
      else mean.normalize();
      mean.toArray(next, v * 3);
    }
    normals.set(next);
  }
}

/**
 * How much material there is straight below each vertex: the distance along
 * the inward normal to wherever the surface comes back. Finite only where the
 * crown is genuinely thin — an incisal edge, or the sliver between a generated
 * interproximal wall and the crown next to it — and that is the one place the
 * wall may not simply take the full offset, because half of it would come out
 * the other side and put a hole in the print.
 */
function materialDepth(
  sheet: Sheet,
  normals: Float64Array,
  reach: number,
  bvh: MeshBVH,
): Float32Array {
  const { points, vertexCount } = sheet;
  // Far enough in to clear the vertex's own triangles, small next to the wall.
  const lift = Math.min(0.01, reach * 0.05);
  const depth = new Float32Array(vertexCount).fill(Infinity);
  const ray = new Ray();
  const normal = new Vector3();
  for (let v = 0; v < vertexCount; v++) {
    normal.fromArray(normals, v * 3);
    if (normal.lengthSq() < 0.5) continue;
    ray.origin.fromArray(points, v * 3).addScaledVector(normal, -lift);
    ray.direction.copy(normal).negate();
    // Raycaster + acceleratedRaycast silently reads no hits here; the BVH's
    // own entry point is the one that works.
    const hit = bvh.raycastFirst(ray, DoubleSide);
    if (hit && hit.distance < reach) depth[v] = lift + hit.distance;
  }
  return depth;
}

/**
 * A BVH over a copy of the sheet, plus that copy's face normals. The copy is
 * because MeshBVH permutes the index buffer it is given — and the normals are
 * read back off the PERMUTED buffer, since that is the order a hit's
 * `faceIndex` refers to.
 */
function surfaceProbe(sheet: Sheet, onBorder: Uint8Array, flipped: boolean) {
  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new Float32BufferAttribute(Float32Array.from(sheet.points), 3));
  geometry.setIndex(new Uint32BufferAttribute(Uint32Array.from(sheet.faces), 1));
  const bvh = new MeshBVH(geometry);

  const index = geometry.getIndex()!;
  const probeFaces = index.count / 3;
  const faceNormals = new Float32Array(probeFaces * 3);
  // Faces touching the open border: inside/outside cannot be read off them.
  // The nearest point to something under the rim IS the rim, and the normal
  // there belongs to whichever face the border edge happens to sit on.
  const atBorder = new Uint8Array(probeFaces);
  const a = new Vector3();
  const b = new Vector3();
  const c = new Vector3();
  const normal = new Vector3();
  for (let f = 0; f < probeFaces; f++) {
    a.fromArray(sheet.points, index.getX(f * 3) * 3);
    b.fromArray(sheet.points, index.getX(f * 3 + 1) * 3);
    c.fromArray(sheet.points, index.getX(f * 3 + 2) * 3);
    normal.crossVectors(b.sub(a), c.sub(a)).normalize();
    // Same flip the vertex normals took: these decide which side is material,
    // and a sheet wound inwards would otherwise send every test the wrong way.
    if (flipped) normal.negate();
    faceNormals[f * 3] = normal.x;
    faceNormals[f * 3 + 1] = normal.y;
    faceNormals[f * 3 + 2] = normal.z;
    for (let e = 0; e < 3; e++) {
      if (onBorder[index.getX(f * 3 + e)]) atBorder[f] = 1;
    }
  }
  return { geometry, bvh, faceNormals, atBorder };
}

/**
 * The true inner offset surface, computed on the mesh's own vertices instead
 * of resampled out of a voxel grid.
 *
 * The set of interior points exactly `thickness` from the surface is what a
 * distance field draws, and it never crosses itself: at a cusp too sharp to
 * hold the wall it does not fold, it creases, and the tip above the crease is
 * simply solid. Fixed topology can express that — the vertices there have to
 * bunch up onto the crease instead of fanning out past it — and this is how
 * they get there: smooth the inner surface, then push every vertex back to
 * exactly `thickness` from the nearest point of the surface. Smoothing
 * collapses the fan, projection restores the thickness, and between them it
 * converges to the trimmed offset with the wall still at full thickness.
 *
 * That is the difference from tapering: at a cusp this keeps the whole wall
 * and makes the tip solid, where pulling the offset back keeps the tip hollow
 * and thins the wall to nothing.
 */
function relaxOffsetSurface(
  base: Sheet,
  normals: Float64Array,
  cap: Float32Array,
  thickness: number,
  passes: number,
  { bvh, faceNormals }: { bvh: MeshBVH; faceNormals: Float32Array },
): Float64Array {
  const { points, vertexCount } = base;
  const { start, neighbours, onBorder } = connectivity(base);

  const inner = new Float64Array(vertexCount * 3);
  for (let v = 0; v < vertexCount; v++) {
    for (let k = 0; k < 3; k++) inner[v * 3 + k] = points[v * 3 + k] - normals[v * 3 + k] * cap[v];
  }

  const smoothed = new Float64Array(inner.length);
  const at = new Vector3();
  const away = new Vector3();
  const anchor = new Vector3();
  const faceNormal = new Vector3();
  const hit = { point: new Vector3(), distance: 0, faceIndex: 0 };
  for (let pass = 0; pass < passes; pass++) {
    for (let v = 0; v < vertexCount; v++) {
      const from = start[v];
      const to = start[v + 1];
      // The cervical ring stays where the plain offset put it: its nearest
      // surface point is the border edge it came from, so projecting it would
      // walk it along the rim instead of holding the wall.
      if (onBorder[v] || to === from) {
        for (let k = 0; k < 3; k++) smoothed[v * 3 + k] = inner[v * 3 + k];
        continue;
      }
      for (let k = 0; k < 3; k++) {
        let sum = 0;
        for (let n = from; n < to; n++) sum += inner[neighbours[n] * 3 + k];
        smoothed[v * 3 + k] = inner[v * 3 + k] + 0.5 * (sum / (to - from) - inner[v * 3 + k]);
      }
    }
    inner.set(smoothed);

    for (let v = 0; v < vertexCount; v++) {
      if (onBorder[v]) continue;
      at.fromArray(inner, v * 3);
      if (!bvh.closestPointToPoint(at, hit)) continue;
      away.subVectors(at, hit.point);
      faceNormal.fromArray(faceNormals, hit.faceIndex * 3);
      // Outside the material, or sitting on the surface with no direction to
      // speak of: step off the face it landed on instead.
      if (away.lengthSq() < 1e-12 || away.dot(faceNormal) > 0) away.copy(faceNormal).negate();
      at.copy(hit.point).addScaledVector(away.normalize(), thickness);

      // Thin material still rules: the wall may never be deeper than the
      // vertex has room for, or it comes out the far side.
      anchor.fromArray(points, v * 3);
      const reach = at.distanceTo(anchor);
      // Written as three steps on purpose: chaining this reads naturally and
      // is wrong, because the argument expression mutates the receiver before
      // the call sees it and every vertex lands on the origin.
      if (reach > cap[v]) {
        at.sub(anchor);
        at.multiplyScalar(cap[v] / reach);
        at.add(anchor);
      }
      at.toArray(inner, v * 3);
    }
  }

  // Polish: whatever is still folded gets smoothed WITHOUT being projected
  // back out. Projection is what holds the thickness, so dropping it lets the
  // last few creases settle — the wall goes locally thinner exactly where the
  // crown is too sharp to carry it, which is the honest answer there anyway.
  const folded = new Uint8Array(vertexCount);
  const p0 = new Vector3();
  const edge1 = new Vector3();
  const edge2 = new Vector3();
  const wasNormal = new Vector3();
  const isNormal = new Vector3();
  for (let round = 0; round < POLISH_ROUNDS; round++) {
    folded.fill(0);
    let count = 0;
    for (let f = 0; f < base.faceCount; f++) {
      const i0 = base.faces[f * 3];
      const i1 = base.faces[f * 3 + 1];
      const i2 = base.faces[f * 3 + 2];
      wasNormal.crossVectors(
        edge1.fromArray(points, i1 * 3).sub(p0.fromArray(points, i0 * 3)),
        edge2.fromArray(points, i2 * 3).sub(p0),
      );
      isNormal.crossVectors(
        edge1.fromArray(inner, i1 * 3).sub(p0.fromArray(inner, i0 * 3)),
        edge2.fromArray(inner, i2 * 3).sub(p0),
      );
      if (wasNormal.dot(isNormal) > wasNormal.lengthSq() * COLLAPSE_RATIO) continue;
      count++;
      folded[i0] = 1;
      folded[i1] = 1;
      folded[i2] = 1;
    }
    if (count === 0) break;
    for (let v = 0; v < vertexCount; v++) {
      const from = start[v];
      const to = start[v + 1];
      if (!folded[v] || onBorder[v] || to === from) {
        for (let k = 0; k < 3; k++) smoothed[v * 3 + k] = inner[v * 3 + k];
        continue;
      }
      for (let k = 0; k < 3; k++) {
        let sum = 0;
        for (let n = from; n < to; n++) sum += inner[neighbours[n] * 3 + k];
        smoothed[v * 3 + k] = inner[v * 3 + k] + 0.7 * (sum / (to - from) - inner[v * 3 + k]);
      }
      // Still capped: without the projection holding it, a crease vertex will
      // happily drift millimetres in and leave a lump of solid behind it.
      at.fromArray(smoothed, v * 3);
      anchor.fromArray(points, v * 3);
      const reach = at.distanceTo(anchor);
      if (reach > cap[v]) {
        at.sub(anchor);
        at.multiplyScalar(cap[v] / reach);
        at.add(anchor);
        at.toArray(smoothed, v * 3);
      }
    }
    inner.set(smoothed);
  }
  return inner;
}

/** Caps the wanted offset wherever the crown has no room for it. */
function capByMaterial(
  target: Float32Array,
  depth: Float32Array,
  minThickness: number,
  fitFactor: number,
): Float32Array {
  const cap = Float32Array.from(target);
  for (let v = 0; v < cap.length; v++) {
    if (depth[v] < Infinity) {
      cap[v] = Math.max(Math.min(cap[v], depth[v] * fitFactor * 0.5), minThickness);
    }
  }
  return cap;
}

/**
 * The offset distance per vertex: the requested thickness, pulled back only
 * where taking it would break the wall.
 *
 * Two things can break it. Thin material — handled above, by halving whatever
 * depth there is. And curvature: a convex ridge whose radius is smaller than
 * the offset turns its triangles inside out on the way in. The second is
 * settled by simply looking, one pass at a time: build the offset, find the
 * faces that came out reversed, pull their vertices back, repeat. That is
 * measurably less pessimistic than the textbook test of whether a ball of the
 * full thickness fits inside the material at the vertex — a ball is blocked by
 * anything anywhere, including a far side that the local triangles never
 * touch, which on a molar tapers a good half of the crown for no reason.
 */
function fitWallRadii(
  sheet: Sheet,
  normals: Float64Array,
  target: Float32Array,
  minThickness: number,
  maxIterations: number,
  lipschitz: number,
): Float32Array {
  const { points, faces, vertexCount, faceCount } = sheet;
  const radius = Float32Array.from(target);

  const inner = new Float64Array(vertexCount * 3);
  const p0 = new Vector3();
  const edge1 = new Vector3();
  const edge2 = new Vector3();
  const before = new Vector3();
  const after = new Vector3();
  const shrink = new Uint8Array(vertexCount);
  for (let pass = 0; pass < maxIterations; pass++) {
    limitTaperGradient(sheet, radius, lipschitz);
    for (let v = 0; v < vertexCount; v++) {
      for (let k = 0; k < 3; k++) {
        inner[v * 3 + k] = points[v * 3 + k] - normals[v * 3 + k] * radius[v];
      }
    }
    shrink.fill(0);
    let folded = 0;
    for (let f = 0; f < faceCount; f++) {
      const i0 = faces[f * 3];
      const i1 = faces[f * 3 + 1];
      const i2 = faces[f * 3 + 2];
      before.crossVectors(
        edge1.fromArray(points, i1 * 3).sub(p0.fromArray(points, i0 * 3)),
        edge2.fromArray(points, i2 * 3).sub(p0),
      );
      after.crossVectors(
        edge1.fromArray(inner, i1 * 3).sub(p0.fromArray(inner, i0 * 3)),
        edge2.fromArray(inner, i2 * 3).sub(p0),
      );
      // Not just "did it reverse" but "did it nearly collapse": a face left
      // with a tenth of its area is a sliver about to reverse on the next
      // vertex that moves.
      if (before.dot(after) > before.lengthSq() * COLLAPSE_RATIO) continue;
      folded++;
      shrink[i0] = 1;
      shrink[i1] = 1;
      shrink[i2] = 1;
    }
    if (folded === 0) break;
    for (let v = 0; v < vertexCount; v++) {
      if (shrink[v]) radius[v] = Math.max(radius[v] * PULLBACK, minThickness);
    }
  }

  return radius;
}

/**
 * The rim of the inner surface, the way the border vertices' own normals draw
 * it, is torn: a border vertex has half its fan missing and the generated
 * interproximal wall's triangles — millimetres wide where the scan's are
 * 0.15 mm — outvote the scan in what is left. Neighbouring vertices step in
 * wildly different directions, and the skirt comes out as spikes. Measured on
 * the fourteen captured teeth, roughly three quarters of every jagged inner
 * vertex sits on or within two rings of the border, at FULL wall thickness:
 * a direction fault, not a depth fault, so no amount of escape repair touches
 * it.
 *
 * The cure is the same thing Blender gets for free by moving everything with
 * one smooth field: the step positions are Laplacian-smoothed along the rim
 * and a couple of rings in, then pushed back out to each vertex's own step
 * length. Smoothing decides the DIRECTION, the offset keeps deciding the
 * depth — the wall thickness stays, only the tearing goes.
 */
function settleRim(
  { points, vertexCount }: Sheet,
  inner: Float64Array,
  start: Uint32Array,
  neighbours: Uint32Array,
  onBorder: Uint8Array,
  minThickness: number,
  passes: number,
): void {
  const RIM_RINGS = 2;
  const ring = new Uint8Array(vertexCount).fill(RIM_RINGS + 1);
  for (let v = 0; v < vertexCount; v++) if (onBorder[v]) ring[v] = 0;
  for (let depth = 1; depth <= RIM_RINGS; depth++) {
    for (let v = 0; v < vertexCount; v++) {
      if (ring[v] <= RIM_RINGS) continue;
      for (let n = start[v]; n < start[v + 1]; n++) {
        if (ring[neighbours[n]] === depth - 1) {
          ring[v] = depth;
          break;
        }
      }
    }
  }

  const length = new Float64Array(vertexCount);
  const anchor = new Vector3();
  const at = new Vector3();
  for (let v = 0; v < vertexCount; v++) {
    if (ring[v] > RIM_RINGS) continue;
    anchor.fromArray(points, v * 3);
    length[v] = at.fromArray(inner, v * 3).distanceTo(anchor);
  }

  // The step LENGTH tears the ring too, not just its direction — in the
  // scaled mode it follows the vertex's facing, which is read off exactly the
  // normals that are garbage here. Smoothed along the rim before it is
  // enforced; ring-2 lengths serve as boundary values and stay put.
  const settledLength = Float64Array.from(length);
  for (let pass = 0; pass < passes; pass++) {
    for (let v = 0; v < vertexCount; v++) {
      if (ring[v] > 1) continue;
      let sum = 0;
      let count = 0;
      for (let n = start[v]; n < start[v + 1]; n++) {
        const other = neighbours[n];
        if (ring[other] > RIM_RINGS) continue;
        sum += length[other];
        count++;
      }
      if (count) settledLength[v] = length[v] + 0.5 * (sum / count - length[v]);
    }
    length.set(settledLength);
  }

  const next = Float64Array.from(inner);
  for (let pass = 0; pass < passes; pass++) {
    for (let v = 0; v < vertexCount; v++) {
      if (ring[v] > RIM_RINGS) continue;
      const from = start[v];
      const to = start[v + 1];
      if (to === from) continue;
      let mx = 0;
      let my = 0;
      let mz = 0;
      let count = 0;
      for (let n = from; n < to; n++) {
        // A border vertex is smoothed along the border CURVE only. Its
        // interior neighbours sit above the rim, and averaging them in tilts
        // every step up under the crown — a systematic lean, not denoising.
        if (ring[v] === 0 && ring[neighbours[n]] !== 0) continue;
        const other = neighbours[n] * 3;
        mx += inner[other];
        my += inner[other + 1];
        mz += inner[other + 2];
        count++;
      }
      if (count === 0) continue;
      at.fromArray(inner, v * 3);
      at.x += 0.5 * (mx / count - at.x);
      at.y += 0.5 * (my / count - at.y);
      at.z += 0.5 * (mz / count - at.z);

      // Back onto the sphere of the vertex's own step, floored so no quad of
      // the skirt degenerates.
      anchor.fromArray(points, v * 3);
      at.sub(anchor);
      const reach = at.length();
      if (reach > 1e-9) at.multiplyScalar(Math.max(length[v], minThickness) / reach);
      at.add(anchor).toArray(next, v * 3);
    }
    inner.set(next);
  }
}

/**
 * The inner surface as an iso-surface of the distance field, one vertex at a
 * time. Each outer vertex starts at the naive normal offset and is walked onto
 * the level set at −thickness (see projectOntoIso).
 *
 * Border vertices keep the plain offset, exactly as every other mode does: the
 * cervical/base ring has no material behind it to be inside of, and the rim
 * bridge pairs it 1:1 with the outer border.
 *
 * `stalled` counts the vertices whose iso-surface did not exist locally —
 * material thinner than two walls. They sit on the medial axis instead, which
 * is what makes those regions come out SOLID rather than as a paper-thin
 * cavity, with no rescue pass involved.
 */
function projectOntoLevelSet(
  base: Sheet,
  normals: Float64Array,
  field: DistanceField,
  thickness: number,
  onBorder: Uint8Array,
  cap: Float32Array,
): { points: Float64Array; depth: Float32Array; stalled: number; solid: Uint8Array } {
  const { points, vertexCount } = base;
  const inner = new Float64Array(vertexCount * 3);
  const depth = new Float32Array(vertexCount);
  const solid = new Uint8Array(vertexCount);
  const anchor = new Vector3();
  const normal = new Vector3();
  const inward = new Vector3();
  // A concavity legitimately needs the offset surface farther in than the wall
  // is thick; past this the march has left the feature it belongs to.
  const maxDepth = thickness * 3;
  const step = Math.min(field.resolutionMm ?? thickness / 8, thickness / 4);
  let stalled = 0;
  for (let v = 0; v < vertexCount; v++) {
    anchor.fromArray(points, v * 3);
    normal.fromArray(normals, v * 3);
    inward.copy(normal).negate();
    if (onBorder[v]) {
      // No material behind the rim to be inside of; the plain offset it is,
      // and the rim bridge pairs it 1:1 with the outer border as always.
      depth[v] = thickness;
    } else {
      const found = depthToIso(field, anchor, inward, -thickness, maxDepth, step);
      if (!found.reached) {
        stalled++;
        // The iso-surface did not exist here: the solid is thinner than two
        // walls, so this vertex stops on the medial axis and the opposing
        // wall stops on the SAME surface. The two inner sheets touching is
        // how the region comes out solid — the identical situation the
        // per-vertex path manufactures with its cavity rescue, and it is
        // marked the same way so the fold and jag metrics treat both alike.
        solid[v] = 1;
      }
      // A ray that never got inside at all is not reporting a thin wall, it is
      // reporting that the field and the mesh disagree about which side is
      // material — a repair patch stretched over a void, a face left wound
      // the wrong way. Its "deepest point" is then an arbitrary spot up to
      // maxDepth away, and following it threw vertices clean across the model
      // (measured: folded faces spanning 30 mm). Fall back to the plain
      // offset there and let the escape check downstream have the last word.
      // …so fall back to what the per-vertex modes would have done here: the
      // material cap, which is already limited by how much room the crown has
      // and graded against its neighbours.
      depth[v] = found.entered ? Math.max(found.depth, 0) : cap[v];
    }
  }

  // The field is smooth, but the DEPTH read off it along a ray is not: at a
  // fissure or an embrasure two neighbouring rays can end on different sides
  // of a medial ridge and disagree by a wall's worth. The same Lipschitz limit
  // the material caps use fixes it, and it only ever LOWERS a depth, so it can
  // never push a vertex out through the far side.
  limitTaperGradient(base, depth, LEVELSET_SLOPE);

  for (let v = 0; v < vertexCount; v++) {
    for (let k = 0; k < 3; k++) {
      inner[v * 3 + k] = points[v * 3 + k] - normals[v * 3 + k] * depth[v];
    }
  }
  return { points: inner, depth, stalled, solid };
}

/**
 * Keeps the taper gradual: an offset whose distance changes faster than the
 * distance between the vertices folds, whatever the per-vertex ball said.
 */
function limitTaperGradient(
  { points, faces, faceCount }: Sheet,
  radius: Float32Array,
  lipschitz: number,
): void {
  for (let pass = 0; pass < 8; pass++) {
    let changed = false;
    for (let f = 0; f < faceCount; f++) {
      for (let e = 0; e < 3; e++) {
        const i = faces[f * 3 + e];
        const j = faces[f * 3 + ((e + 1) % 3)];
        const dx = points[i * 3] - points[j * 3];
        const dy = points[i * 3 + 1] - points[j * 3 + 1];
        const dz = points[i * 3 + 2] - points[j * 3 + 2];
        const slack = Math.sqrt(dx * dx + dy * dy + dz * dz) * lipschitz;
        if (radius[i] > radius[j] + slack) {
          radius[i] = radius[j] + slack;
          changed = true;
        } else if (radius[j] > radius[i] + slack) {
          radius[j] = radius[i] + slack;
          changed = true;
        }
      }
    }
    if (!changed) return;
  }
}

/**
 * Directed edges with no oppositely wound partner — the sheet's open border,
 * which becomes the rim of the shell. Kept as a list rather than a map keyed
 * by the start vertex: where two border loops touch, one vertex has two open
 * edges leaving it and a map would silently drop one, leaving a hole.
 */
function borderEdgesOf(outer: Uint32Array, vertexCount: number): number[] {
  const faceCount = outer.length / 3;
  const directed = new Set<number>();
  for (let f = 0; f < faceCount; f++) {
    for (let e = 0; e < 3; e++) {
      directed.add(outer[f * 3 + e] * vertexCount + outer[f * 3 + ((e + 1) % 3)]);
    }
  }
  const pairs: number[] = [];
  for (let f = 0; f < faceCount; f++) {
    for (let e = 0; e < 3; e++) {
      const from = outer[f * 3 + e];
      const to = outer[f * 3 + ((e + 1) % 3)];
      if (!directed.has(to * vertexCount + from)) pairs.push(from, to);
    }
  }
  return pairs;
}

function countLoops(borderPairs: number[]): number {
  const next = new Map<number, number>();
  for (let e = 0; e < borderPairs.length; e += 2) next.set(borderPairs[e], borderPairs[e + 1]);
  const walked = new Set<number>();
  let loops = 0;
  for (const start of next.keys()) {
    if (walked.has(start)) continue;
    loops++;
    let at: number | undefined = start;
    while (at !== undefined && !walked.has(at)) {
      walked.add(at);
      at = next.get(at);
    }
  }
  return loops;
}

export function buildOffsetShell(
  sheet: BufferGeometry,
  options: OffsetShellOptions,
): OffsetShellResult {
  const thickness = options.thicknessMm;
  const minThickness = options.minThicknessMm ?? Math.min(0.05, thickness * 0.2);
  const plain = options.fit === 'plain';
  // A level set needs no help from any of the guards below: it is smooth,
  // cannot fold and cannot leave the solid, so the denoising, the material
  // caps, the step-field smoothing and the cavity rescue are all switched off
  // for it. What stays is the rim settle and the escape check, and the latter
  // only as an assertion — on a correct field it finds nothing.
  const levelSet = options.fit === 'levelset';
  if (levelSet && !options.field) {
    throw new Error("buildOffsetShell: fit 'levelset' requires a distance field");
  }
  const source = readSheet(sheet);
  const { points, faces, vertexCount, faceCount } = source;
  // Everything about the inner wall — its direction, how deep it fits, where
  // it ends up — comes off the denoised copy. The outer surface below is
  // still the source, untouched. A plain extrude denoises nothing: the point
  // of it is to be exactly what the mesh says. Neither does a level set: it
  // is measured against the field, which was built from the real surface.
  const smoothing = options.innerSmoothingPasses ?? (plain || levelSet ? 0 : 4);
  const shiftLimit = thickness * 0.1;
  const base: Sheet = { ...source, points: smoothedBase(source, smoothing, shiftLimit) };
  const { start, neighbours, onBorder } = connectivity(base);
  const { normals, flipped } = outwardNormals(base);
  // The inner surface is `p − n·depth`, so an inner edge is
  // `(p_i − p_j) − depth·(n_i − n_j)`: it inverts as soon as the NORMAL field
  // turns faster than 1/depth, which on a scan's own noise scale it does all
  // over the crowns. For the per-vertex modes the cure costs accuracy, since
  // the direction also decides where the wall ends up. Not for a level set:
  // there the direction only has to be smooth — the DEPTH comes from the
  // field, so wherever the ray points, it stops exactly `thickness` inside.
  // So this mode can afford to diffuse hard, and gets a fold-free inner
  // surface for nothing.
  if (!plain) {
    diffuseNormals(vertexCount, normals, start, neighbours, levelSet ? 64 : 4);
  }

  const even = (options.evenThickness ?? plain)
    ? evenThicknessFactors(base, normals, options.evenThicknessClamp ?? 2)
    : new Float32Array(vertexCount).fill(1);

  // Denoising moves the base off the real surface, and the wall is measured
  // from the real one, so the shift comes out of the offset before anything
  // else looks at it — otherwise the print is thicker than asked by however
  // much the smoothing moved.
  const target = new Float32Array(vertexCount);
  for (let v = 0; v < vertexCount; v++) {
    let shift = 0;
    for (let k = 0; k < 3; k++) {
      shift += (points[v * 3 + k] - base.points[v * 3 + k]) * normals[v * 3 + k];
    }
    const wanted = thickness - Math.min(Math.max(shift, -shiftLimit), shiftLimit);
    target[v] = Math.max(minThickness, wanted) * even[v];
  }

  const probe = surfaceProbe(base, onBorder, flipped);
  // The algorithm works against the denoised copy, but the wall a print gets
  // is measured against the REAL surface — and so is the question of whether
  // the inner wall came out through it.
  const outerProbe = surfaceProbe(source, onBorder, flipped);
  const scratchAt = new Vector3();
  const scratchNormal = new Vector3();
  const cap = plain
    ? target
    : capByMaterial(
        target,
        materialDepth(base, normals, thickness * 2, probe.bvh),
        minThickness,
        options.fitFactor ?? 0.9,
      );
  // A cap is a per-vertex answer, and per-vertex answers step: a vertex over a
  // thin sliver gets cut to the floor while its neighbour keeps the full wall,
  // and that cliff in the inner surface is a spike. Grading it only ever
  // LOWERS a neighbour, so it cannot push anything out the far side.
  if (!plain) limitTaperGradient(base, cap, CAP_SLOPE);

  let innerPoints: Float64Array;
  let isoStalled = 0;
  let levelSetSolid: Uint8Array | null = null;
  if (levelSet) {
    const result = projectOntoLevelSet(base, normals, options.field!, thickness, onBorder, cap);
    innerPoints = result.points;
    isoStalled = result.stalled;
    levelSetSolid = result.solid;
  } else if (options.fit === 'shrink') {
    // Move every vertex at the mesh's own centre. The direction is then one
    // globally consistent field instead of a per-vertex normal, which is what
    // makes it immune to the normals that point the wrong way — and exactly
    // what selecting everything and scaling it down does.
    //
    // Area-weighted centre, not the average vertex: an intraoral scan samples
    // the occlusal table far more densely than the axial walls, and a plain
    // vertex average is dragged up into it.
    const centre = new Vector3();
    const corner = [new Vector3(), new Vector3(), new Vector3()];
    let area = 0;
    for (let f = 0; f < faceCount; f++) {
      for (let e = 0; e < 3; e++) corner[e].fromArray(base.points, faces[f * 3 + e] * 3);
      const weight =
        scratchAt
          .subVectors(corner[1], corner[0])
          .cross(scratchNormal.subVectors(corner[2], corner[0]))
          .length() / 2;
      scratchAt.copy(corner[0]).add(corner[1]).add(corner[2]).divideScalar(3);
      centre.addScaledVector(scratchAt, weight);
      area += weight;
    }
    centre.divideScalar(Math.max(area, 1e-9));

    // No single centre works everywhere: a crown is not star-shaped, and on
    // about a twentieth of it the direction to the centre points OUT through
    // the surface. Those vertices are the ones that visibly lift off the top
    // of the tooth, and they are the whole thin tail as well, since the escape
    // repair then tucks them back to the minimum. There the vertex's own
    // normal is right, so the direction blends over to it — blended, not
    // switched, because a step change in the displacement field folds.
    const blendBelow = Math.max(options.radialBlend ?? RADIAL_BLEND, 1e-3);
    const facing = new Float64Array(vertexCount);
    const radii = new Float64Array(vertexCount);
    const square: number[] = [];
    for (let v = 0; v < vertexCount; v++) {
      scratchAt.fromArray(base.points, v * 3).sub(centre);
      radii[v] = scratchAt.length();
      if (radii[v] < 1e-9) continue;
      facing[v] = scratchAt.dot(scratchNormal.fromArray(normals, v * 3)) / radii[v];
      if (facing[v] > blendBelow) square.push(radii[v] * facing[v]);
    }
    square.sort((x, y) => x - y);
    // Uniform scale sized so the MEDIAN wall lands on target; with even
    // thickness on, each vertex instead steps as far as ITS own angle needs.
    const uniform = thickness / Math.max(square.length ? square[square.length >> 1] : 1, 1e-6);
    const clamp = options.evenThicknessClamp ?? 2;
    const evenRadial = options.evenThickness ?? true;

    const direction = new Vector3();
    innerPoints = new Float64Array(vertexCount * 3);
    for (let v = 0; v < vertexCount; v++) {
      scratchNormal.fromArray(normals, v * 3);
      const radial = Math.min(Math.max(facing[v] / blendBelow, 0), 1);
      if (radii[v] > 1e-9) {
        direction.fromArray(base.points, v * 3).sub(centre).divideScalar(-radii[v]);
      } else {
        direction.copy(scratchNormal).negate();
      }
      direction.multiplyScalar(radial).addScaledVector(scratchNormal, -(1 - radial));
      if (direction.lengthSq() < 1e-12) direction.copy(scratchNormal).negate();
      direction.normalize();

      const along = Math.max(-direction.dot(scratchNormal), 1 / clamp);
      const step = evenRadial
        ? Math.min(thickness / along, thickness * clamp)
        : uniform * radii[v] * radial + thickness * (1 - radial);
      for (let k = 0; k < 3; k++) {
        const axis = k === 0 ? direction.x : k === 1 ? direction.y : direction.z;
        innerPoints[v * 3 + k] = base.points[v * 3 + k] + axis * step;
      }
    }
  } else if (options.fit !== 'guarded' && !plain) {
    innerPoints = relaxOffsetSurface(
      base,
      normals,
      cap,
      thickness,
      options.relaxPasses ?? 16,
      probe,
    );
  } else {
    const radius =
      plain || options.maxIterations === 0
        ? cap
        : fitWallRadii(
            base,
            normals,
            cap,
            minThickness,
            options.maxIterations ?? 24,
            options.lipschitz ?? 2,
          );
    innerPoints = new Float64Array(vertexCount * 3);
    for (let v = 0; v < vertexCount; v++) {
      for (let k = 0; k < 3; k++) {
        innerPoints[v * 3 + k] = base.points[v * 3 + k] - normals[v * 3 + k] * radius[v];
      }
    }
  }
  if (!plain) settleRim(source, innerPoints, start, neighbours, onBorder, minThickness, 12);

  const outPositions = new Float32Array(vertexCount * 6);
  const achieved = new Float32Array(vertexCount);
  for (let v = 0; v < vertexCount; v++) {
    const inner = (vertexCount + v) * 3;
    for (let k = 0; k < 3; k++) {
      outPositions[v * 3 + k] = points[v * 3 + k];
      outPositions[inner + k] = innerPoints[v * 3 + k];
    }
  }

  // Last line of defence, and the only fault that can reach the part: an
  // inner-surface vertex that ended up OUTSIDE the crown has pushed the wall
  // through the surface someone looks at. Any that are left get pulled back
  // under it. A plain extrude is exempt — the point of that mode is to be
  // exactly what an unguarded offset does, warts and all.
  const escaped = new Vector3();
  const escapedDelta = new Vector3();
  const escapedAnchor = new Vector3();
  const escapedNormal = new Vector3();
  const escapedHit = { point: new Vector3(), distance: 0, faceIndex: 0 };
  let outsideVertices = 0;
  let outsideDepth = 0;
  let outsideIds: number[] = [];
  const repairRounds = plain ? 0 : (options.escapeRounds ?? ESCAPE_ROUNDS);

  // The repair below only ever SHORTENS steps, and shortens them as a FIELD:
  // the cut a flagged vertex takes is spread over its neighbourhood at a
  // bounded slope before it is applied. Both previous repairs moved one vertex
  // at a time — onto the nearest surface point first, back along its own step
  // later — and both manufactured spikes, because a lone vertex pulled away
  // from neighbours that stay put IS a spike, whichever way it is pulled.
  const stepDir = new Float64Array(vertexCount * 3);
  const stepLen = new Float32Array(vertexCount);
  for (let v = 0; v < vertexCount; v++) {
    escapedAnchor.fromArray(outPositions, v * 3);
    escaped.fromArray(outPositions, (vertexCount + v) * 3).sub(escapedAnchor);
    const len = escaped.length();
    stepLen[v] = len;
    if (len > 1e-9) escaped.divideScalar(len);
    else escaped.fromArray(normals, v * 3).negate();
    escaped.toArray(stepDir, v * 3);
  }

  // The wall the user sees from inside is the FIELD of step lengths: where
  // caps, taper cuts and projections disagree between neighbouring vertices,
  // the inner surface is pitted even though every single vertex is doing the
  // right thing locally. Smoothing the SCALAR field — never the positions —
  // keeps the inner wall following the outer shape exactly (that is what an
  // extrude means) while the offset distance varies smoothly instead of
  // jumping vertex to vertex. Clamped to the material cap so a raise can
  // never punch out the far side, and the escape repair right below guards
  // whatever is left.
  if (!plain && !levelSet && options.fit !== 'shrink') {
    // Only where the field actually jumps: smoothing flat on-target regions
    // costs median thickness for nothing (measured: 89% → 84% within 10% of
    // target with unmasked smoothing), while the pits live exactly at the
    // vertices whose step disagrees with their ring.
    const jumpy = new Uint8Array(vertexCount);
    const jumpSpan = Math.max(0.05, thickness * 0.1);
    for (let v = 0; v < vertexCount; v++) {
      const from = start[v];
      const to = start[v + 1];
      if (onBorder[v] || to === from) continue;
      let sum = 0;
      for (let n = from; n < to; n++) sum += stepLen[neighbours[n]];
      if (Math.abs(stepLen[v] - sum / (to - from)) > jumpSpan) jumpy[v] = 1;
    }
    // One ring of context, so the blend has somewhere to fade into.
    const dilated = Uint8Array.from(jumpy);
    for (let v = 0; v < vertexCount; v++) {
      if (!jumpy[v]) continue;
      for (let n = start[v]; n < start[v + 1]; n++) dilated[neighbours[n]] = 1;
    }

    const smoothedLen = new Float32Array(vertexCount);
    for (let pass = 0; pass < (options.stepSmoothingPasses ?? 6); pass++) {
      for (let v = 0; v < vertexCount; v++) {
        const from = start[v];
        const to = start[v + 1];
        if (!dilated[v] || onBorder[v] || to === from) {
          smoothedLen[v] = stepLen[v];
          continue;
        }
        let sum = 0;
        for (let n = from; n < to; n++) sum += stepLen[neighbours[n]];
        const wanted = stepLen[v] + 0.5 * (sum / (to - from) - stepLen[v]);
        smoothedLen[v] = Math.min(Math.max(wanted, minThickness), cap[v]);
      }
      stepLen.set(smoothedLen);
    }
    for (let v = 0; v < vertexCount; v++) {
      if (!dilated[v] || onBorder[v]) continue;
      for (let k = 0; k < 3; k++) {
        outPositions[(vertexCount + v) * 3 + k] =
          outPositions[v * 3 + k] + stepDir[v * 3 + k] * stepLen[v];
      }
    }
  }

  // An edge can have both ends buried and still bulge out through the surface
  // in between. Testing vertices alone is blind to that, and it is what a
  // spike looks like from outside.
  const midpointEscapes = (i: number, j: number) => {
    escaped
      .fromArray(outPositions, (vertexCount + i) * 3)
      .add(escapedDelta.fromArray(outPositions, (vertexCount + j) * 3))
      .multiplyScalar(0.5);
    if (escaped.distanceTo(escapedDelta) * 2 <= STRETCH_SPAN) return false;
    if (!outerProbe.bvh.closestPointToPoint(escaped, escapedHit)) return false;
    if (outerProbe.atBorder[escapedHit.faceIndex]) return false;
    escapedNormal.fromArray(outerProbe.faceNormals, escapedHit.faceIndex * 3);
    return escapedDelta.subVectors(escaped, escapedHit.point).dot(escapedNormal) > ESCAPE_EPSILON;
  };

  let outsideEdges = 0;
  const cut = new Float32Array(vertexCount);
  for (let round = 0; round <= repairRounds; round++) {
    outsideVertices = 0;
    outsideDepth = 0;
    outsideIds = [];
    for (let v = 0; v < vertexCount; v++) {
      const inner = (vertexCount + v) * 3;
      escaped.fromArray(outPositions, inner);
      if (!outerProbe.bvh.closestPointToPoint(escaped, escapedHit)) continue;
      // The wall is how far the inner surface is from the outer one — NOT how
      // far the vertex travelled. They are the same only when it moved along
      // the normal; a radial step of the same length leaves a thinner wall.
      // At the cervical ring the nearest surface is the rim the vertex came
      // from, which reads zero and means nothing, so there it is the bridge
      // height that counts.
      if (onBorder[v]) {
        let gap = 0;
        for (let k = 0; k < 3; k++) {
          gap += (outPositions[v * 3 + k] - outPositions[inner + k]) ** 2;
        }
        achieved[v] = Math.sqrt(gap);
      } else {
        achieved[v] = escapedHit.distance;
      }
      // Inside/outside cannot be read against a face at the open border: the
      // nearest point to something under the rim IS the rim.
      if (outerProbe.atBorder[escapedHit.faceIndex]) continue;
      escapedNormal.fromArray(outerProbe.faceNormals, escapedHit.faceIndex * 3);
      // Note the separate vector: doing this as escaped.sub(...) would leave
      // `escaped` holding a difference instead of the position, which is a bug
      // this file has now paid for twice.
      const out = escapedDelta.subVectors(escaped, escapedHit.point).dot(escapedNormal);
      if (out <= ESCAPE_EPSILON) continue;
      outsideVertices++;
      outsideDepth = Math.max(outsideDepth, out);
      outsideIds.push(v);
    }
    outsideEdges = 0;
    const bulgingEnds: number[] = [];
    for (let f = 0; f < faceCount; f++) {
      for (let e = 0; e < 3; e++) {
        const i = faces[f * 3 + e];
        const j = faces[f * 3 + ((e + 1) % 3)];
        if (i > j || !midpointEscapes(i, j)) continue;
        outsideEdges++;
        bulgingEnds.push(i, j);
      }
    }
    if ((outsideVertices === 0 && outsideEdges === 0) || round === repairRounds) break;

    cut.fill(0);
    for (const v of [...outsideIds, ...bulgingEnds]) {
      cut[v] = Math.max(cut[v], stepLen[v] * 0.5);
    }
    // Spread each cut outwards at a bounded slope, so the difference between
    // neighbouring steps stays below what an eye or a printer resolves.
    for (let pass = 0; pass < 8; pass++) {
      let changed = false;
      for (let f = 0; f < faceCount; f++) {
        for (let e = 0; e < 3; e++) {
          const i = faces[f * 3 + e];
          const j = faces[f * 3 + ((e + 1) % 3)];
          const dx = points[i * 3] - points[j * 3];
          const dy = points[i * 3 + 1] - points[j * 3 + 1];
          const dz = points[i * 3 + 2] - points[j * 3 + 2];
          const slack = Math.sqrt(dx * dx + dy * dy + dz * dz) * REPAIR_SLOPE;
          if (cut[i] > cut[j] + slack) {
            cut[j] = cut[i] - slack;
            changed = true;
          } else if (cut[j] > cut[i] + slack) {
            cut[i] = cut[j] - slack;
            changed = true;
          }
        }
      }
      if (!changed) break;
    }
    for (let v = 0; v < vertexCount; v++) {
      if (cut[v] <= 0) continue;
      const len = Math.max(stepLen[v] - cut[v], minThickness);
      if (len >= stepLen[v]) continue;
      stepLen[v] = len;
      for (let k = 0; k < 3; k++) {
        outPositions[(vertexCount + v) * 3 + k] =
          outPositions[v * 3 + k] + stepDir[v * 3 + k] * len;
      }
    }
  }
  // ---- thin-cavity rescue -------------------------------------------------
  // Where the SOLID is thinner than two walls — the embrasures and the
  // papilla band along the crown bases — hollowing squeezes a paper-thin
  // CAVITY slot between the two inner sheets, and the slicer renders it as
  // holes along the gum-tooth junction. The honest shape there is SOLID:
  // deepen the inner surface until the opposing inner sheets cross (the
  // inverted pocket unions away in the slicer) while never crossing the
  // opposing OUTER surface, which would be a visible escape. Raises ramp at
  // the same bounded slope as the cuts above — a lone deepened vertex is a
  // spike on the cavity side — and are verified per vertex against the outer
  // surface, with failures rolled back before anything moves.
  let solidifiedVertices = 0;
  const solidMark = levelSetSolid ?? new Uint8Array(vertexCount);
  if (levelSetSolid) solidifiedVertices = isoStalled;
  // Solidification belongs to the production mode only. The comparison modes
  // exist to show what an unguarded/scaled offset does — and in 'shrink' the
  // radial steps turn the blunt pointwise raises into counted jags on every
  // ring of neighbours (measured 44 on a tooth whose relaxed shell reads 0).
  if (!plain && !levelSet && options.fit !== 'shrink' && options.fit !== 'guarded') {
    const rescueRay = new Ray();
    const raise = new Float32Array(vertexCount);
    const thin = thickness * 0.55;
    for (let v = 0; v < vertexCount; v++) {
      if (onBorder[v]) continue;
      if (achieved[v] >= thin) continue;
      if (stepLen[v] >= thickness) continue;
      // Material room along this vertex's own step, measured from the inner
      // position: the first outer-surface crossing ahead is the far side.
      // NO hit means there is no opposing surface — the vertex is thin
      // because the TAPER pulled it back at a sharp open feature (a ridge,
      // a rim sliver), and deepening there re-creates the exact fold the
      // taper exists to prevent. Solidification is only honest between two
      // real outer surfaces.
      rescueRay.origin.fromArray(outPositions, (vertexCount + v) * 3);
      rescueRay.direction.fromArray(stepDir, v * 3);
      const hit = outerProbe.bvh.raycastFirst(rescueRay, DoubleSide);
      if (!hit) continue;
      const room = hit.distance - Math.max(0.1, 4 * ESCAPE_EPSILON);
      if (room <= 0) continue;
      raise[v] = Math.min(room, thickness - stepLen[v]);
    }

    // Unlike a cut, a raise may never grow a neighbour that cannot afford
    // one, so the slope limit only pulls raises DOWN toward the un-raised rim
    // of the region.
    const limitRaise = () => {
      for (let pass = 0; pass < 8; pass++) {
        let changed = false;
        for (let f = 0; f < faceCount; f++) {
          for (let e = 0; e < 3; e++) {
            const i = faces[f * 3 + e];
            const j = faces[f * 3 + ((e + 1) % 3)];
            const dx = points[i * 3] - points[j * 3];
            const dy = points[i * 3 + 1] - points[j * 3 + 1];
            const dz = points[i * 3 + 2] - points[j * 3 + 2];
            const slack = Math.sqrt(dx * dx + dy * dy + dz * dz) * REPAIR_SLOPE;
            if (raise[i] > raise[j] + slack) {
              raise[i] = raise[j] + slack;
              changed = true;
            } else if (raise[j] > raise[i] + slack) {
              raise[j] = raise[i] + slack;
              changed = true;
            }
          }
        }
        if (!changed) break;
      }
    };
    limitRaise();

    for (let round = 0; round < 2; round++) {
      let dropped = 0;
      for (let v = 0; v < vertexCount; v++) {
        if (raise[v] <= 1e-6) {
          raise[v] = 0;
          continue;
        }
        const len = stepLen[v] + raise[v];
        escaped.set(
          outPositions[v * 3] + stepDir[v * 3] * len,
          outPositions[v * 3 + 1] + stepDir[v * 3 + 1] * len,
          outPositions[v * 3 + 2] + stepDir[v * 3 + 2] * len,
        );
        if (!outerProbe.bvh.closestPointToPoint(escaped, escapedHit)) continue;
        if (outerProbe.atBorder[escapedHit.faceIndex]) {
          raise[v] = 0;
          dropped++;
          continue;
        }
        escapedNormal.fromArray(outerProbe.faceNormals, escapedHit.faceIndex * 3);
        const out = escapedDelta.subVectors(escaped, escapedHit.point).dot(escapedNormal);
        if (out > -ESCAPE_EPSILON) {
          raise[v] = 0;
          dropped++;
        }
      }
      if (dropped === 0) break;
      limitRaise();
    }

    for (let v = 0; v < vertexCount; v++) {
      if (raise[v] <= 1e-6) continue;
      solidifiedVertices++;
      solidMark[v] = 1;
      const len = stepLen[v] + raise[v];
      stepLen[v] = len;
      for (let k = 0; k < 3; k++) {
        outPositions[(vertexCount + v) * 3 + k] =
          outPositions[v * 3 + k] + stepDir[v * 3 + k] * len;
      }
      // The region is solid now: what the print carries there is the step
      // itself, and that is what the thickness stats should read.
      achieved[v] = Math.min(thickness, len);
    }

    // ---- blunt cavity terminations ----------------------------------------
    // The raises above solidify wherever the material is thinner than two
    // walls, but along the CONTOUR where it is just about two walls the
    // cavity still narrows into a zero-width wedge, and the slicer renders
    // that boundary as a thin broken ring — better than the band it replaced,
    // still a defect. So wherever the cavity ahead of a vertex is narrower
    // than a nozzle can print, cross it outright. These raises are pointwise
    // ON PURPOSE: they end buried inside solid material where no eye or
    // printer reaches, and ramping them would just re-create the sub-floor
    // wedge they remove.
    {
      const innerGeometry = new BufferGeometry();
      const innerPoints = new Float32Array(vertexCount * 3);
      innerPoints.set(outPositions.subarray(vertexCount * 3));
      innerGeometry.setAttribute('position', new Float32BufferAttribute(innerPoints, 3));
      innerGeometry.setIndex(new Uint32BufferAttribute(Uint32Array.from(faces), 1));
      const innerBvh = new MeshBVH(innerGeometry);
      const cavityFloor = Math.max(0.4, 0.35 * thickness);
      const margin = Math.max(0.1, 4 * ESCAPE_EPSILON);
      for (let v = 0; v < vertexCount; v++) {
        if (onBorder[v] || solidMark[v]) continue;
        rescueRay.origin.fromArray(outPositions, (vertexCount + v) * 3);
        rescueRay.direction.fromArray(stepDir, v * 3);
        // First hit past the vertex's own fan is the opposing inner sheet —
        // but only when it comes BEFORE the opposing outer surface; past it,
        // the cavity ahead belongs to a different region entirely.
        const outerHit = outerProbe.bvh.raycastFirst(rescueRay, DoubleSide);
        if (!outerHit) continue;
        let gap = Infinity;
        for (const hit of innerBvh.raycast(rescueRay, DoubleSide)) {
          if (hit.distance > 0.02 && hit.distance < gap) gap = hit.distance;
        }
        if (gap >= cavityFloor || gap >= outerHit.distance) continue;
        const extra = gap + 0.15;
        if (extra + margin > outerHit.distance) continue;
        const len = stepLen[v] + extra;
        escaped.set(
          outPositions[v * 3] + stepDir[v * 3] * len,
          outPositions[v * 3 + 1] + stepDir[v * 3 + 1] * len,
          outPositions[v * 3 + 2] + stepDir[v * 3 + 2] * len,
        );
        if (!outerProbe.bvh.closestPointToPoint(escaped, escapedHit)) continue;
        if (outerProbe.atBorder[escapedHit.faceIndex]) continue;
        escapedNormal.fromArray(outerProbe.faceNormals, escapedHit.faceIndex * 3);
        if (escapedDelta.subVectors(escaped, escapedHit.point).dot(escapedNormal) > -ESCAPE_EPSILON) {
          continue;
        }
        solidifiedVertices++;
        solidMark[v] = 1;
        stepLen[v] = len;
        for (let k = 0; k < 3; k++) {
          outPositions[(vertexCount + v) * 3 + k] =
            outPositions[v * 3 + k] + stepDir[v * 3 + k] * len;
        }
        achieved[v] = Math.min(thickness, len);
      }
      innerGeometry.dispose();
    }
  }

  probe.geometry.dispose();
  outerProbe.geometry.dispose();

  let tapered = 0;
  for (let v = 0; v < vertexCount; v++) {
    if (achieved[v] < thickness * 0.9) tapered++;
  }

  let jaggedVertices = 0;
  {
    const meanInner = new Vector3();
    const meanOuter = new Vector3();
    for (let v = 0; v < vertexCount; v++) {
      const from = start[v];
      const to = start[v + 1];
      if (to === from) continue;
      // Solidified vertices are buried inside SOLID material by construction
      // (the blunt-termination raises are pointwise on purpose) — nothing an
      // eye or a printer can reach, which is the fault this metric measures.
      if (solidMark[v]) continue;
      meanInner.set(0, 0, 0);
      meanOuter.set(0, 0, 0);
      for (let n = from; n < to; n++) {
        const outerAt = neighbours[n] * 3;
        const innerAt = (vertexCount + neighbours[n]) * 3;
        meanOuter.x += outPositions[outerAt];
        meanOuter.y += outPositions[outerAt + 1];
        meanOuter.z += outPositions[outerAt + 2];
        meanInner.x += outPositions[innerAt];
        meanInner.y += outPositions[innerAt + 1];
        meanInner.z += outPositions[innerAt + 2];
      }
      meanOuter.divideScalar(to - from);
      meanInner.divideScalar(to - from);
      const off =
        escaped.fromArray(outPositions, (vertexCount + v) * 3).distanceTo(meanInner) -
        escapedDelta.fromArray(outPositions, v * 3).distanceTo(meanOuter);
      if (off > JAG_SPAN) jaggedVertices++;
    }
  }

  // Outer faces wound so their normals leave the material; the inner copy is
  // the same triangles reversed.
  const outer = new Uint32Array(faceCount * 3);
  for (let f = 0; f < faceCount; f++) {
    outer[f * 3] = faces[f * 3];
    outer[f * 3 + 1] = faces[f * 3 + (flipped ? 2 : 1)];
    outer[f * 3 + 2] = faces[f * 3 + (flipped ? 1 : 2)];
  }

  const borderPairs = borderEdgesOf(outer, vertexCount);
  const borderEdges = borderPairs.length / 2;
  const outIndex = new Uint32Array((faceCount * 2 + borderEdges * 2) * 3);
  outIndex.set(outer, 0);
  for (let f = 0; f < faceCount; f++) {
    const base = (faceCount + f) * 3;
    outIndex[base] = vertexCount + outer[f * 3];
    outIndex[base + 1] = vertexCount + outer[f * 3 + 2];
    outIndex[base + 2] = vertexCount + outer[f * 3 + 1];
  }
  // Rim: each open edge becomes the quad (from, from', to', to). Walking the
  // border in the outer surface's winding keeps the material on the left, so
  // this order faces away from it.
  let cursor = faceCount * 2 * 3;
  for (let e = 0; e < borderPairs.length; e += 2) {
    const from = borderPairs[e];
    const to = borderPairs[e + 1];
    outIndex[cursor++] = from;
    outIndex[cursor++] = vertexCount + from;
    outIndex[cursor++] = vertexCount + to;
    outIndex[cursor++] = from;
    outIndex[cursor++] = vertexCount + to;
    outIndex[cursor++] = to;
  }

  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new Float32BufferAttribute(outPositions, 3));
  geometry.setIndex(new Uint32BufferAttribute(outIndex, 1));

  const folds = countFolds(faces, base.points, outPositions, vertexCount, achieved, solidMark);

  let stretchRatio = 0;
  let stretchSpan = 0;
  let stretchedEdges = 0;
  const stretchSeen = new Set<number>();
  for (let f = 0; f < faceCount; f++) {
    for (let e = 0; e < 3; e++) {
      const i = faces[f * 3 + e];
      const j = faces[f * 3 + ((e + 1) % 3)];
      const key = Math.min(i, j) * vertexCount + Math.max(i, j);
      if (stretchSeen.has(key)) continue;
      stretchSeen.add(key);
      let was = 0;
      let is = 0;
      for (let k = 0; k < 3; k++) {
        was += (points[i * 3 + k] - points[j * 3 + k]) ** 2;
        is += (outPositions[(vertexCount + i) * 3 + k] - outPositions[(vertexCount + j) * 3 + k]) ** 2;
      }
      if (was < 1e-12) continue;
      const ratio = Math.sqrt(is / was);
      const span = Math.sqrt(is);
      // A big ratio on its own means nothing — the source edge may have been
      // microscopic. What shows in a print is an inner edge that is both much
      // longer than its source and long in absolute terms.
      if (ratio <= 3 || span <= STRETCH_SPAN) continue;
      stretchRatio = Math.max(stretchRatio, ratio);
      stretchSpan = Math.max(stretchSpan, span);
      stretchedEdges++;
    }
  }
  const sorted = Float32Array.from(achieved).sort();
  return {
    geometry,
    vertexCount,
    faceCount,
    thickness: achieved,
    minThicknessMm: sorted.length ? sorted[0] : 0,
    medianThicknessMm: sorted.length ? sorted[sorted.length >> 1] : 0,
    taperedVertices: tapered,
    foldedFaces: folds.folded,
    foldDepthMm: folds.depth,
    foldSpanMm: folds.span,
    outsideVertices,
    outsideDepthMm: outsideDepth,
    outsideVertexIds: Uint32Array.from(outsideIds),
    stretchRatio,
    stretchSpanMm: stretchSpan,
    stretchedEdges,
    outsideEdges,
    jaggedVertices,
    solidifiedVertices,
    borderEdges,
    borderLoops: countLoops(borderPairs),
    flipped,
    capField: cap,
    targetField: target,
    stepField: stepLen,
    solidField: solidMark,
    isoStalledVertices: isoStalled,
  };
}

/**
 * Edges of an indexed geometry that are not shared by exactly two oppositely
 * wound triangles — i.e. the ways it fails to be a printable solid.
 */
export function countOpenEdges(geometry: BufferGeometry): number {
  const index = geometry.getIndex();
  if (!index) return 0;
  const vertexCount = geometry.getAttribute('position').count;
  const counts = new Map<number, number>();
  for (let i = 0; i < index.count; i += 3) {
    for (let e = 0; e < 3; e++) {
      const from = index.getX(i + e);
      const to = index.getX(i + ((e + 1) % 3));
      const key = from * vertexCount + to;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
  }
  let open = 0;
  for (const [key, count] of counts) {
    const from = Math.floor(key / vertexCount);
    const to = key - from * vertexCount;
    if (count !== 1 || (counts.get(to * vertexCount + from) ?? 0) !== 1) open++;
  }
  return open;
}

/**
 * Faces the offset turned inside out on the way in. Measured against the
 * surface the wall was actually offset FROM, not the outer one: on a noisy
 * scan those differ, and the question here is only whether the offset map
 * folded.
 */
function countFolds(
  faces: Uint32Array,
  base: Float64Array,
  inner: Float32Array,
  vertexCount: number,
  reach: Float32Array,
  /** Vertices the thin-cavity rescue solidified: their folds are the FIX. */
  solidMark?: Uint8Array,
): { folded: number; depth: number; span: number } {
  const faceCount = faces.length / 3;
  const p0 = new Vector3();
  const edge1 = new Vector3();
  const edge2 = new Vector3();
  const before = new Vector3();
  const after = new Vector3();
  let folded = 0;
  let depth = 0;
  let span = 0;
  const corner = [new Vector3(), new Vector3(), new Vector3()];
  for (let f = 0; f < faceCount; f++) {
    const i0 = faces[f * 3];
    const i1 = faces[f * 3 + 1];
    const i2 = faces[f * 3 + 2];
    // Crossing the opposing inner sheet is HOW a solidified region prints
    // solid; counting that as damage would gate out the very fix. Anything
    // touching a solidified vertex sits inside such a pocket by construction.
    if (solidMark && (solidMark[i0] || solidMark[i1] || solidMark[i2])) continue;
    before.crossVectors(
      edge1.fromArray(base, i1 * 3).sub(p0.fromArray(base, i0 * 3)),
      edge2.fromArray(base, i2 * 3).sub(p0),
    );
    const offset = vertexCount * 3;
    after.crossVectors(
      edge1.fromArray(inner, offset + i1 * 3).sub(p0.fromArray(inner, offset + i0 * 3)),
      edge2.fromArray(inner, offset + i2 * 3).sub(p0),
    );
    if (before.dot(after) > 0) continue;
    folded++;
    depth = Math.max(depth, reach[i0], reach[i1], reach[i2]);
    corner[0].fromArray(inner, offset + i0 * 3);
    corner[1].fromArray(inner, offset + i1 * 3);
    corner[2].fromArray(inner, offset + i2 * 3);
    span = Math.max(
      span,
      corner[0].distanceTo(corner[1]),
      corner[1].distanceTo(corner[2]),
      corner[2].distanceTo(corner[0]),
    );
  }
  return { folded, depth, span };
}

/** Signed volume in mm³. Negative means the solid is inside out. */
export function signedVolume(geometry: BufferGeometry): number {
  const index = geometry.getIndex();
  const position = geometry.getAttribute('position');
  if (!index) return 0;
  const p0 = new Vector3();
  const p1 = new Vector3();
  const p2 = new Vector3();
  const cross = new Vector3();
  let volume = 0;
  for (let i = 0; i < index.count; i += 3) {
    p0.fromBufferAttribute(position, index.getX(i));
    p1.fromBufferAttribute(position, index.getX(i + 1));
    p2.fromBufferAttribute(position, index.getX(i + 2));
    volume += p0.dot(cross.crossVectors(p1, p2)) / 6;
  }
  return volume;
}
